import React, { Component, createRef, Dispatch } from 'react';
import { connect } from 'react-redux';
import {
    SofaButton,
    SofaInput,
    AbstractSofaForm,
    maxLengthFilter,
    SofaForm,
    SofaFormControl,
    SofaFormControlDescription,
    getPropsForSofaInput,
    textFilter
} from '@sofology/sofologycomponents';

import './BasketDeliveryCost.scss';

import { AppState } from 'store';
import { PostcodeModel, DeliveryTypes } from 'models';
import { BasketErrorModel } from 'models/Errors';
import { BasketDetailsModel, DeliveryModeCodes } from 'models/Basket';
import { Loader } from 'components';
import {
    generateDispatchToPropsMap,
    scrollHelper,
    errorToBasketErrorModel,
    NOT_ALLOW_DOUBLE_SPACES_REGEX,
    getEstimatedDeliveryDate
} from 'helpers';
import { formatCurrency } from 'formatters';
import {
    CalculateDeliveryCostFunc,
    RemoveCalculateDeliveryCostErrorFunc,
    calculateDeliveryCost,
    removeCalculateDeliveryCostError,
    SetDeliveryTypeFunc,
    setDeliveryType
} from 'store/basket/actions';
import { Validatable, Scrollable } from 'interfaces';

const MIN_POSTCODE_LENGTH = 2;
const COURIER_DELIVERY_PRICE = 199;
const ENTER_MODE_ERROR_MESSAGE = 'Please enter a postcode and click Check button';
const PRICE_APPROVE_ERROR_MESSAGE = 'Please click Proceed button to confirm your agreement with the Delivery cost';
const EDIT_MODE_ERROR_MESSAGE = 'Please enter a postcode and confirm the changes';
const POSTCODE_NOT_EMPTY_ERROR_MESSAGE = 'At least 2 characters needed';
const POSTCODE_MAX_LENGTH = 10;

const ENTER_EVENT_KEY = 'Enter';

interface OwnProps {
    ref?: React.RefObject<BasketDeliveryCostType>;
}

interface StateProps {
    basket: BasketDetailsModel;
    serverError: BasketErrorModel | null;
    isCalculating: boolean;
    waitsForChoosingDeliveryType: boolean;
    savedPostcode: string | undefined;
}

interface OwnState {
    form: SofaForm<BasketDeliveryCostFormControls>;
    isEditMode: boolean;
    calculatePostcodeClicked: boolean;
    postcodeError: string | null;
}

interface DispatchProps {
    calculateDeliveryCost: CalculateDeliveryCostFunc;
    removeCalculateDeliveryCostError: RemoveCalculateDeliveryCostErrorFunc;
    setDeliveryType: SetDeliveryTypeFunc;
}

interface BasketDeliveryCostFormControls extends AbstractSofaForm {
    postcode: SofaFormControl<string>;
}

enum DeliveryCostMode {
    Enter = 'EnterMode',
    View = 'ViewMode',
    PriceApprove = 'PriceApproveMode',
    Edit = 'EditMode'
}

export class BasketDeliveryCost extends Component<StateProps & DispatchProps & OwnProps, OwnState> implements Validatable, Scrollable {
    private element = React.createRef<HTMLDivElement>();

    constructor(props: StateProps & DispatchProps & OwnProps) {
        super(props);

        this.state = {
            form: new SofaForm<BasketDeliveryCostFormControls>(
                {
                    postcode: {
                        name: 'postcode',
                        defaultValue: '',
                        filters: [
                            maxLengthFilter(POSTCODE_MAX_LENGTH),
                            textFilter(NOT_ALLOW_DOUBLE_SPACES_REGEX, ' ')
                        ],
                        validators: [
                            this.validatePostcodeNotEmpty,
                            this.validatePostcodeNotNull,
                            this.postcodeServerValidator,
                            this.stateHasPostcodeError
                        ],
                        ref: createRef<SofaInput>(),
                    } as SofaFormControlDescription<string>
                },
                updateStateFunc => {
                    this.setState(updateStateFunc);
                },
                'form',
                () => this.state,
                errorToBasketErrorModel
            ),
            isEditMode: false,
            calculatePostcodeClicked: false,
            postcodeError: null
        };
    }

    componentDidUpdate(prevProps: StateProps & DispatchProps & OwnProps, prevState: OwnState) {
        if (this.state.isEditMode && prevProps.isCalculating && !this.props.isCalculating && !this.props.serverError) {
            this.setState({ isEditMode: false });
        }

        if (prevState.form.controls.postcode.value !== this.state.form.controls.postcode.value && this.state.postcodeError) {
            this.setState({ postcodeError: null });
            this.props.removeCalculateDeliveryCostError();
            this.state.form.controls.postcode.validate();
        }

        if (this.props.serverError && this.state.calculatePostcodeClicked) {
            this.setState({
                calculatePostcodeClicked: false,
                postcodeError: this.props.serverError.message
            });
            this.state.form.controls.postcode.validate();
        }
    }

    handleClickCalculateDeliveryCost = async () => {
        this.setState({
            calculatePostcodeClicked: true,
            postcodeError: null
        });
        await this.state.form.controls.postcode.validate();

        if (this.state.form.controls.postcode.value && !this.state.form.controls.postcode.errorMessage) {
            this.props.calculateDeliveryCost(new PostcodeModel(this.state.form.controls.postcode.value));
        }
    };

    handleStartEditingPostcode = () => {
        this.state.form.controls.postcode.setValue(this.props.basket.deliveryAddress!.postcode.code);
        this.setState({
            isEditMode: true,
            postcodeError: null
        });
    };

    handleApproveDeliveryCost = () => {
        this.props.setDeliveryType(DeliveryTypes.NonMainlandDelivery);
        this.setState({ postcodeError: null });
    };

    handleKeyDownEvent = (e: React.KeyboardEvent) => {
        if (e.key === ENTER_EVENT_KEY) {
            this.handleClickCalculateDeliveryCost();
        }
    };

    render(): JSX.Element {
        const mode = this.getMode();

        return (
            <div className='basket-delivery-cost' ref={this.element}>
                {mode === DeliveryCostMode.Enter && this.renderEnterMode()}
                {mode === DeliveryCostMode.View && this.renderViewMode()}
                {mode === DeliveryCostMode.PriceApprove && this.renderDeliveryCostApprove()}
                {mode === DeliveryCostMode.Edit && this.renderEditMode()}
                <Loader show={this.props.isCalculating} overlay={true} />
            </div>
        );
    }

    renderEnterMode(): JSX.Element {
        return (
            <>
                <div className='basket-delivery-cost__enter-mode'>
                    <div className='basket-delivery-cost__enter-mode-title'>Delivery cost</div>
                    <div className='basket-delivery-cost__calculation-wrapper'>
                        <div className='basket-delivery-cost__calculation-input'>
                            <div className='delivery-cost-input'>
                                <SofaInput
                                    label='Enter your postcode'
                                    inputProps={{ 'aria-label': 'Enter your postcode',
                                        autoCorrect: 'off',
                                        autoComplete: 'postal-code' }}
                                    {...getPropsForSofaInput(this.state.form.controls.postcode)}
                                    helperText={this.state.form.controls.postcode.errorMessage}
                                    helperTextType='error'
                                    onKeyDown={this.handleKeyDownEvent}
                                />
                            </div>
                        </div>
                        <div className='basket-delivery-cost__calculation-button'>
                            <SofaButton
                                text='Check'
                                variant='secondary'
                                onClick={this.handleClickCalculateDeliveryCost}
                            />
                        </div>
                    </div>
                </div>
            </>
        );
    }

    renderViewMode(): JSX.Element {
        const shouldRenderPrice = !this.isUserCollectFromWarehouse() && !this.isUserCollectFromStore();
        const editButtonText = <>Edit <i className='sa sa-pencil-square-o'></i></>;

        return (
            <div className='basket-delivery-cost__view-mode'>
                <div className='basket-delivery-cost__view-mode-title'>
                    {this.renderDeliveryToTitleText()}
                    <div className='basket-delivery-cost__view-mode-code'>
                        {this.props.basket.deliveryAddress!.postcode.code}
                    </div>
                    <div className='basket-delivery-cost__view-mode-button'>
                        <SofaButton
                            text={editButtonText}
                            variant='small'
                            onClick={this.handleStartEditingPostcode}
                        />
                    </div>
                </div>
                <div className='basket-delivery-cost__view-mode-results'>
                    <div className='basket-delivery-cost__view-mode-time'>
                        Your order comes with delivery from just {getEstimatedDeliveryDate(this.props.basket.leadTime, this.props.basket.hasQuickstockItem())}
                    </div>
                    {shouldRenderPrice && this.renderPrice()}
                    {this.isUserCollectFromStore()
                        && <div className='basket-delivery-cost__view-mode-price'>Collect from Store</div>}
                    {this.isUserCollectFromWarehouse()
                        && <div className='basket-delivery-cost__view-mode-price'>Collect from Warehouse</div>}
                </div>
            </div>
        );
    }

    renderEditMode(): JSX.Element {
        return (
            <div className='basket-delivery-cost__edit-mode'>
                {this.renderDeliveryToTitleText()}
                <div className='basket-delivery-cost__calculation-wrapper'>
                    <div className='basket-delivery-cost__calculation-input'>
                        <div className='delivery-cost-input'>
                            <SofaInput
                                label='Postcode'
                                inputProps={{ autoCorrect: 'off', autoComplete: 'postal-code' }}
                                {...getPropsForSofaInput(this.state.form.controls.postcode)}
                                helperText={this.state.postcodeError ?? this.state.form.controls.postcode.errorMessage}
                                helperTextType='error'
                                onKeyDown={this.handleKeyDownEvent}
                            />
                        </div>
                    </div>
                    <div className='basket-delivery-cost__calculation-button'>
                        <SofaButton
                            text='Apply'
                            variant='secondary'
                            onClick={this.handleClickCalculateDeliveryCost}
                        />
                    </div>
                </div>
            </div>
        );
    }

    renderDeliveryToTitleText(): JSX.Element {
        return (
            <div className='basket-delivery-cost__delivery-to-title-text'>
                <div className='basket-delivery-cost__delivery-to-title-icon'>
                    <i className='sa sa-delivery-fast'></i>
                </div>
                Delivery to:
            </div>
        );
    }

    renderPrice(): JSX.Element {
        return (
            <div className='basket-delivery-cost__view-mode-price'>
                {this.props.basket.deliveryCost!.value > 0 && 'only '}
                {formatCurrency(this.props.basket.deliveryCost!.value)}
            </div>
        );
    }

    renderDeliveryCostApprove(): JSX.Element {
        return (
            <>
                <div className='basket-delivery-cost__cost-approve-wrapper'>
                    <div className='basket-delivery-cost__cost-approve-title'>
                        Non UK mainland address
                    </div>
                    <div className='basket-delivery-cost__cost-approve-price-row'>
                        <div className='basket-delivery-cost__cost-approve-price-title'>
                            Delivery by Courier:
                        </div>
                        <div className='basket-delivery-cost__cost-approve-price-value'>
                            {formatCurrency(COURIER_DELIVERY_PRICE, 0)}
                        </div>
                    </div>
                    <div className='basket-delivery-cost__cost-approve-button'>
                        <SofaButton
                            text='Proceed'
                            variant='secondary'
                            onClick={this.handleApproveDeliveryCost}
                        />
                    </div>
                </div>
                {this.renderError()}
            </>
        );
    }

    renderError(): JSX.Element {
        return (
            <div className='basket-delivery-cost__error'>
                {this.props.serverError
                    ? this.formatErrorMessage(this.props.serverError.message)
                    : this.state.postcodeError &&
                    this.formatErrorMessage(this.state.postcodeError)}
            </div>
        );
    }

    getMode(): DeliveryCostMode | null {
        const isEnterMode = !this.props.basket.deliveryMode && !this.props.waitsForChoosingDeliveryType;
        const isViewMode = this.props.basket.deliveryMode && !this.state.isEditMode && !this.props.waitsForChoosingDeliveryType;
        const isPriceApproveMode = this.props.waitsForChoosingDeliveryType;
        const isEditMode = this.state.isEditMode && !this.props.waitsForChoosingDeliveryType;

        return isEnterMode && DeliveryCostMode.Enter
            || isViewMode && DeliveryCostMode.View
            || isPriceApproveMode && DeliveryCostMode.PriceApprove
            || isEditMode && DeliveryCostMode.Edit
            || null;
    }

    formatErrorMessage(message: string): string {
        return message.replace(/(postal code)/ig, 'Postcode');
    }

    postcodeServerValidator = (): Promise<string | null> => {
        return Promise.resolve(
            this.props.serverError
                ? this.formatErrorMessage(this.props.serverError.message)
                : null
        );
    };

    stateHasPostcodeError = (): string | null => {
        if (this.props.serverError) {
            return this.formatErrorMessage(this.props.serverError.message);
        }

        if (this.state?.postcodeError) {
            return this.formatErrorMessage(this.state.postcodeError);
        }

        return null;
    };

    validatePostcodeNotNull = (value: string): string | null => {
        if (!value?.length) {
            const mode = this.getMode();
            switch (mode) {
                case DeliveryCostMode.Enter:
                    return ENTER_MODE_ERROR_MESSAGE;
                case DeliveryCostMode.PriceApprove:
                    return PRICE_APPROVE_ERROR_MESSAGE;
                case DeliveryCostMode.Edit:
                    return EDIT_MODE_ERROR_MESSAGE;
            }
        }

        return null;
    };

    validatePostcodeNotEmpty = (value: string): string | null => {
        if (Boolean(value) && value.length < MIN_POSTCODE_LENGTH) {
            return POSTCODE_NOT_EMPTY_ERROR_MESSAGE;
        }

        return null;
    };

    isUserCollectFromStore(): boolean {
        return Boolean(
            this.props.basket.deliveryCost
            && this.props.basket.deliveryCost.value === 0
            && this.props.basket.deliveryMode?.code !== DeliveryModeCodes.NonMainland
            && !this.props.basket.hasDeliverableProducts()
        );
    }

    isUserCollectFromWarehouse(): boolean {
        // seems it works only if we have only Dwell products in the basket + postcode for nonMainland
        // haven't found other cases
        return Boolean(
            this.props.basket.deliveryCost
            && this.props.basket.deliveryCost.value === 0
            && this.props.basket.deliveryMode?.code === DeliveryModeCodes.NonMainland
        );
    }

    validate = (): boolean => {
        const mode = this.getMode();
        const isValid = mode === DeliveryCostMode.View;

        switch (mode) {
            case DeliveryCostMode.Enter:
                if (this.state.form.controls.postcode.value !== this.props.savedPostcode && !this.props.isCalculating) {
                    this.setState({ postcodeError: ENTER_MODE_ERROR_MESSAGE });
                }

                break;
            case DeliveryCostMode.PriceApprove:
                if (!this.props.isCalculating) {
                    this.setState({ postcodeError: PRICE_APPROVE_ERROR_MESSAGE });
                }

                break;
            case DeliveryCostMode.Edit:
                if (!this.props.isCalculating) {
                    this.setState({ postcodeError: EDIT_MODE_ERROR_MESSAGE });
                }

                break;
        }

        this.state.form.controls.postcode.validate();

        return isValid;
    };

    scrollTo = () => {
        scrollHelper.scrollToExcludeFooterHeader(this.element);
    };
}

function mapStateToProps(state: AppState): StateProps {
    return {
        serverError: state.basketState.deliveryCostError,
        isCalculating: state.basketState.isCalculatingDeliveryCost,
        basket: state.basketState.basket!,
        waitsForChoosingDeliveryType: state.basketState.waitsForChoosingDeliveryType,
        savedPostcode: state.basketState.basket?.deliveryAddress?.postcode.code
    };
}

function mapDispatchToProps(dispatch: Dispatch<any>): DispatchProps {
    return generateDispatchToPropsMap(dispatch, [calculateDeliveryCost, removeCalculateDeliveryCostError, setDeliveryType]);
}

export default connect(mapStateToProps, mapDispatchToProps, undefined, { forwardRef: true })(BasketDeliveryCost);
export type BasketDeliveryCostType = BasketDeliveryCost;